// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { XBadgeModel } from "@ces/XDL/types";
import { parseBoolean } from "@ces/XDL/Utilities";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import {
    BadgeImagePlacementValues,
    BadgeModel,
    BadgeStyleValues,
    BadgeTypeValues,
} from "@insite/client-framework/Types/ApiModels";

interface Props {
    productContext: ProductContextModel;
    showTextBadges?: boolean;
    showImageBadges?: boolean;
}
export interface TagsType {
    isPreviouslyPurchased?: string;
}
export const previouslyPurchasedTagId = "previouslyPurchased";
const defaultTag: BadgeModel = {
    badgeColorHexCode: "",
    badgeType: BadgeTypeValues.Text,
    badgeStyle: BadgeStyleValues.Round,
    detailWidgetBadgeSize: "",
    displayOnBadgeWidget: true,
    displayOnProductImages: true,
    displayText: "",
    id: "",
    imageAltText: "",
    largeImageBadgePath: "",
    largeImagePlacement: BadgeImagePlacementValues.None,
    largeImageTextSize: "",
    name: "",
    tagName: "",
    otherImageBadgePath: "",
    otherImagePlacement: BadgeImagePlacementValues.None,
    otherImageTextSize: "",
    sortOrder: -1,
    textColorHexCode: "",
};
const sortBadges = (badges: XBadgeModel[] | null) => {
    return badges?.sort(badge => badge.sortOrder);
};
const xUseWidgetBadges = ({ productContext, showTextBadges, showImageBadges }: Props) => {
    const { textBadges, imageBadges } = useWidgetBadges({ productContext, showImageBadges, showTextBadges });
    if (!showTextBadges) {
        return {
            imageBadges: sortBadges(imageBadges),
        };
    }
    let textBadgesAndTags = sortBadges(textBadges);
    const isPreviouslyPurchased = (productContext.product?.properties as TagsType)?.isPreviouslyPurchased;
    // Previously purchased has the highest priority
    if (parseBoolean(isPreviouslyPurchased)) {
        const prevPurchasedTag: XBadgeModel = {
            ...defaultTag,
            displayText: translate("Prev. Purchase"),
            id: previouslyPurchasedTagId,
            name: translate("Previously Purchased"),
            tagName: translate("Previously Purchased"),
            textColorHexCode: "000000",
            badgeColorHexCode: "FFC18C",
        };
        // add it at the beginning
        if (!textBadgesAndTags) {
            textBadgesAndTags = [];
        }
        textBadgesAndTags?.unshift(prevPurchasedTag);
    }
    return {
        textBadges: textBadgesAndTags,
        imageBadges: sortBadges(imageBadges),
    };
};
export default xUseWidgetBadges;
